import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowRight, Film, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fadeUp } from "./anim";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const resolveSrc = (cfg) => {
  if (!cfg?.video_url) return null;
  return cfg.video_url.startsWith("/") ? `${BACKEND_URL}${cfg.video_url}` : cfg.video_url;
};
const resolveThumb = (cfg) => {
  if (!cfg?.thumbnail_url) return null;
  return cfg.thumbnail_url.startsWith("/") ? `${BACKEND_URL}${cfg.thumbnail_url}` : cfg.thumbnail_url;
};
const getEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0&modestbranding=1`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return null;
};

export const WalkthroughVideo = () => {
  const [cfg, setCfg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [ar, setAr] = useState(null); // native aspect ratio width/height
  const videoRef = useRef(null);

  useEffect(() => {
    api.get("/site/walkthrough")
      .then((r) => setCfg(r.data))
      .catch(() => setCfg({ configured: false }))
      .finally(() => setLoaded(true));
  }, []);

  const src = cfg ? resolveSrc(cfg) : null;
  const embed = cfg?.video_type === "url" ? getEmbed(cfg.video_url) : null;
  const isEmbed = !!embed;
  const poster = cfg ? resolveThumb(cfg) : null;

  // Auto-detect the native aspect ratio without downloading the full video:
  // prefer the thumbnail image's dimensions; otherwise probe video metadata only.
  useEffect(() => {
    if (!cfg?.configured) return;
    let cancelled = false;
    if (poster) {
      const img = new Image();
      img.onload = () => { if (!cancelled && img.naturalHeight) setAr(img.naturalWidth / img.naturalHeight); };
      img.src = poster;
    } else if (src && !isEmbed) {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => { if (!cancelled && v.videoHeight) setAr(v.videoWidth / v.videoHeight); };
      v.src = src;
    }
    return () => { cancelled = true; };
  }, [cfg, poster, src, isEmbed]);

  // Stop playback + reset when the modal closes; lock body scroll while open.
  useEffect(() => {
    if (open) {
      const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
      document.addEventListener("keydown", onKey);
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    }
    if (videoRef.current) {
      try { videoRef.current.pause(); videoRef.current.currentTime = 0; } catch (e) { /* noop */ }
    }
  }, [open]);

  if (!loaded) return null;
  if (!cfg?.configured) return null; // hide section entirely when no video

  const effectiveAr = ar || (isEmbed ? 16 / 9 : 16 / 9);
  const isPortrait = effectiveAr < 1;
  // Portrait: compact centred player (≈400px). Landscape: wide player.
  const wrapperMaxWidth = isPortrait ? "min(400px, 92vw)" : "56rem";

  const refineFromVideo = (e) => {
    const v = e.currentTarget;
    if (v.videoHeight) setAr(v.videoWidth / v.videoHeight);
  };

  return (
    <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6" data-testid="walkthrough-section">
      <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
          <Film className="h-3.5 w-3.5 text-primary" /> Product walkthrough
        </span>
        <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl" data-testid="walkthrough-heading">
          How HostGuard works in 60 seconds
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          See how easy it is to create your free account, complete your first Booking Risk Assessment and access your saved reports.
        </p>
      </motion.div>

      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="mt-10">
        <div className="mx-auto" style={{ maxWidth: wrapperMaxWidth }}>
          {/* Thumbnail in the video's native aspect ratio + centred animated play button. */}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="group relative block w-full overflow-hidden rounded-[18px] border bg-primary shadow-[0_24px_60px_rgba(15,23,42,0.22)]"
            style={{ aspectRatio: `${effectiveAr}` }}
            aria-label="Play walkthrough video"
            data-testid="walkthrough-open"
          >
            {poster ? (
              <img src={poster} alt="HostGuard walkthrough" loading="lazy" className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]" />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-primary to-[hsl(222,55%,26%)]" />
            )}
            <span className="absolute inset-0 bg-black/25 transition-colors duration-200 group-hover:bg-black/35" />
            <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/60 opacity-75" />
              <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-white/95 shadow-xl transition-transform duration-200 group-hover:scale-110">
                <Play className="ml-1 h-8 w-8 fill-primary text-primary" />
              </span>
            </span>
          </button>
        </div>

        <div className="mt-8 flex justify-center">
          <Link to="/signup">
            <Button size="lg" className="gap-2" data-testid="walkthrough-cta">Create Free Account <ArrowRight className="h-4 w-4" /></Button>
          </Link>
        </div>
      </motion.div>

      {/* Centred modal with dark blurred backdrop; native aspect ratio, never cropped. */}
      {open && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 p-4 backdrop-blur-md"
          onClick={() => setOpen(false)}
          data-testid="walkthrough-modal"
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={() => setOpen(false)}
            className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20"
            aria-label="Close video"
            data-testid="walkthrough-close"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="relative overflow-hidden rounded-2xl bg-black shadow-2xl"
            style={{ aspectRatio: `${effectiveAr}`, maxHeight: "88vh", maxWidth: "min(96vw, 1200px)", height: isPortrait ? "88vh" : "auto", width: isPortrait ? "auto" : "min(96vw, 1200px)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {isEmbed ? (
              <iframe
                src={embed}
                title="HostGuard walkthrough"
                className="h-full w-full"
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                data-testid="walkthrough-iframe"
              />
            ) : (
              <video
                ref={videoRef}
                src={src}
                poster={poster || undefined}
                controls
                autoPlay
                controlsList="nodownload"
                playsInline
                onLoadedMetadata={refineFromVideo}
                className="h-full w-full bg-black object-contain"
                data-testid="walkthrough-video-el"
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
};
